import Phaser from "phaser";
import type { AvatarType } from "@/ui/PlayerCustomization";

export const AVATAR_ATLAS_KEY = "avatar-atlas";
export const AVATAR_FRAME_W = 32;
export const AVATAR_FRAME_H = 48;
export const AVATAR_FRAMES = 8;

const FOOT_OFFSET_FROM_BOTTOM = 4;

/**
 * Generates 8-direction spritesheets for all avatar types at runtime and registers
 * them as Phaser textures. Per-player colouring is done via PlayerSprite tint.
 */
export function ensureAvatarAtlas(scene: Phaser.Scene): void {
  const types: AvatarType[] = ["default", "wizard", "warrior", "sorceress"];
  for (const type of types) {
    const key = atlasKeyFor(type);
    if (scene.textures.exists(key)) continue;

    const totalW = AVATAR_FRAME_W * AVATAR_FRAMES;
    const totalH = AVATAR_FRAME_H;

    const canvas = scene.textures.createCanvas(key, totalW, totalH);
    if (!canvas) throw new Error(`createCanvas failed for ${key}`);

    const ctx = canvas.context;

    for (let i = 0; i < AVATAR_FRAMES; i++) {
      drawAvatarFrame(ctx, i, AVATAR_FRAME_W * i, 0, type);
    }

    canvas.refresh();

    for (let i = 0; i < AVATAR_FRAMES; i++) {
      canvas.add(`d${i}`, 0, AVATAR_FRAME_W * i, 0, AVATAR_FRAME_W, AVATAR_FRAME_H);
    }
  }
}

export function atlasKeyFor(type: AvatarType): string {
  if (type === "default") return AVATAR_ATLAS_KEY;
  return `avatar-atlas-${type}`;
}

function drawAvatarFrame(ctx: CanvasRenderingContext2D, frame: number, ox: number, oy: number, type: AvatarType): void {
  const w = AVATAR_FRAME_W;
  const h = AVATAR_FRAME_H;
  const cx = ox + w / 2;
  const feetY = oy + h - FOOT_OFFSET_FROM_BOTTOM;

  ctx.save();

  // Soft drop shadow ellipse on the ground.
  ctx.fillStyle = "rgba(0,0,0,0.32)";
  ctx.beginPath();
  ctx.ellipse(cx, feetY, 11, 4, 0, 0, Math.PI * 2);
  ctx.fill();

  if (type === "default") {
    drawVillager(ctx, cx, feetY, frame);
  } else if (type === "wizard") {
    drawWizard(ctx, cx, feetY, frame);
  } else if (type === "warrior") {
    drawWarrior(ctx, cx, feetY, frame);
  } else if (type === "sorceress") {
    drawSorceress(ctx, cx, feetY, frame);
  }

  ctx.restore();
}

function drawVillager(ctx: CanvasRenderingContext2D, cx: number, feetY: number, frame: number): void {
  const bodyTopY = feetY - 22;
  const headCY = feetY - 30;

  ctx.fillStyle = "#cfcfcf";
  ctx.strokeStyle = "#1a1a1a";
  ctx.lineWidth = 1.5;
  roundedRect(ctx, cx - 7, bodyTopY, 14, 22, 4);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = "#e6e6e6";
  ctx.beginPath();
  ctx.arc(cx, headCY, 6, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  drawFacingIndicator(ctx, cx, bodyTopY + 11, frame);
}

function drawWizard(ctx: CanvasRenderingContext2D, cx: number, feetY: number, frame: number): void {
  const bodyTopY = feetY - 24;
  const headCY = feetY - 30;

  // Robe (trapezoid)
  ctx.fillStyle = "#b8b8d0";
  ctx.strokeStyle = "#1a1a1a";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(cx - 5, bodyTopY);
  ctx.lineTo(cx + 5, bodyTopY);
  ctx.lineTo(cx + 9, feetY);
  ctx.lineTo(cx - 9, feetY);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Head
  ctx.fillStyle = "#e6e6e6";
  ctx.beginPath();
  ctx.arc(cx, headCY, 5, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Pointy hat
  ctx.fillStyle = "#7070a0";
  ctx.beginPath();
  ctx.moveTo(cx, feetY - 44);
  ctx.lineTo(cx - 7, feetY - 28);
  ctx.lineTo(cx + 7, feetY - 28);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  drawFacingIndicator(ctx, cx, bodyTopY + 10, frame);
}

function drawWarrior(ctx: CanvasRenderingContext2D, cx: number, feetY: number, frame: number): void {
  const bodyTopY = feetY - 22;
  const headCY = feetY - 30;

  // Broad body
  ctx.fillStyle = "#c0c0b0";
  ctx.strokeStyle = "#1a1a1a";
  ctx.lineWidth = 1.5;
  roundedRect(ctx, cx - 9, bodyTopY, 18, 22, 3);
  ctx.fill();
  ctx.stroke();

  // Shoulder pads
  ctx.fillStyle = "#a0a090";
  ctx.fillRect(cx - 11, bodyTopY + 2, 4, 6);
  ctx.fillRect(cx + 7, bodyTopY + 2, 4, 6);
  ctx.strokeRect(cx - 11, bodyTopY + 2, 4, 6);
  ctx.strokeRect(cx + 7, bodyTopY + 2, 4, 6);

  // Head
  ctx.fillStyle = "#e6e6e6";
  ctx.beginPath();
  ctx.arc(cx, headCY, 6, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Helmet
  ctx.fillStyle = "#8a8a7a";
  ctx.beginPath();
  ctx.moveTo(cx - 7, headCY);
  ctx.lineTo(cx - 7, headCY - 7);
  ctx.lineTo(cx + 7, headCY - 7);
  ctx.lineTo(cx + 7, headCY);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  drawFacingIndicator(ctx, cx, bodyTopY + 11, frame);
}

function drawSorceress(ctx: CanvasRenderingContext2D, cx: number, feetY: number, frame: number): void {
  const bodyTopY = feetY - 24;
  const headCY = feetY - 30;

  // Dress (A-line)
  ctx.fillStyle = "#d0b8e0";
  ctx.strokeStyle = "#1a1a1a";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(cx - 4, bodyTopY);
  ctx.lineTo(cx + 4, bodyTopY);
  ctx.lineTo(cx + 10, feetY);
  ctx.lineTo(cx - 10, feetY);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Head
  ctx.fillStyle = "#e6e6e6";
  ctx.beginPath();
  ctx.arc(cx, headCY, 5, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Long hair
  ctx.fillStyle = "#c89af0";
  ctx.beginPath();
  ctx.moveTo(cx - 6, headCY - 2);
  ctx.quadraticCurveTo(cx - 8, headCY + 10, cx - 6, headCY + 16);
  ctx.lineTo(cx - 4, headCY + 16);
  ctx.quadraticCurveTo(cx - 6, headCY + 8, cx - 4, headCY);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(cx + 6, headCY - 2);
  ctx.quadraticCurveTo(cx + 8, headCY + 10, cx + 6, headCY + 16);
  ctx.lineTo(cx + 4, headCY + 16);
  ctx.quadraticCurveTo(cx + 6, headCY + 8, cx + 4, headCY);
  ctx.closePath();
  ctx.fill();

  drawFacingIndicator(ctx, cx, bodyTopY + 10, frame);
}

function drawFacingIndicator(ctx: CanvasRenderingContext2D, cx: number, cy: number, frame: number): void {
  const angle = (frame * Math.PI) / 4;
  const dx = Math.cos(angle);
  const dy = Math.sin(angle);
  const ix = cx + dx * 8;
  const iy = cy + dy * 6;
  ctx.fillStyle = "#2a2a2a";
  ctx.beginPath();
  ctx.moveTo(ix + dx * 4, iy + dy * 4);
  ctx.lineTo(ix - dy * 3, iy + dx * 3);
  ctx.lineTo(ix + dy * 3, iy - dx * 3);
  ctx.closePath();
  ctx.fill();
}

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
